import React, { useState } from 'react';
import { X, FileText, AlertTriangle, ChevronDown, ChevronUp, Download, ExternalLink, CheckCircle } from 'lucide-react';

const CGU_CONTENT = `CONDITIONS GÉNÉRALES D'UTILISATION

Service de tests d'intrusion, simulations d'attaque et audit de sécurité

1. OBJET

Les présentes conditions générales d'utilisation (ci-après les "CGU") ont pour objet de définir les conditions dans lesquelles :
- La société [Nom du Prestataire], notamment le cas échéant SYLink Technologie SAS, fournit des services de tests d'intrusion, simulations d'attaques et audits de sécurité (ci-après les "Tests") sur les systèmes d'information de ses clients ;
- Le Client accepte l'exécution de ces Tests ainsi que les risques associés, tels que décrits aux présentes.

Toute demande, commande, signature de devis, validation de plan de test ou utilisation du service vaut acceptation pleine et entière des présentes CGU.

2. DEFINITIONS

Aux fins des présentes CGU, on entend par :
- Prestataire : la société [Nom du Prestataire] (incluant, le cas échéant, SYLink Technologie SAS) qui réalise les Tests.
- Client : toute personne morale ou entité qui mandate le Prestataire pour réaliser des Tests sur son système d'information.
- Tests : l'ensemble des opérations de sécurité offensives (pentest, simulations d'attaque, exploitation de failles, etc.) définies entre le Prestataire et le Client.
- Périmètre : ensemble des systèmes, réseaux, applications, environnements et services sur lesquels les Tests sont autorisés.

3. ACCEPTATION DES CGU

Le Client déclare :
1. Avoir pris connaissance des présentes CGU avant toute validation de prestation ;
2. Les accepter sans réserve ;
3. Être propriétaire, gestionnaire légitime ou dûment autorisé à faire réaliser des Tests sur les systèmes inclus dans le Périmètre.

En cas de contradiction entre les présentes CGU et un contrat spécifique signé entre le Prestataire et le Client, les dispositions contractuelles particulières prévalent, sauf mention contraire.

4. DESCRIPTION GÉNÉRALE DES TESTS

Les Tests peuvent inclure notamment, sans que cette liste soit exhaustive :
- Scans de ports et de vulnérabilités ;
- Tentatives contrôlées d'authentification, de contournement de restrictions et d'élévation de privilèges ;
- Exploitation de vulnérabilités connues ou nouvellement identifiées, dans un cadre maîtrisé ;
- Tests de robustesse et de charge ciblés sur certains services ;
- Analyse et manipulation de trafic réseau (écoute, replay, injection contrôlée) ;
- Tests sur réseaux internes, réseaux externes, applications Web, API, Wi-Fi, systèmes industriels/OT, etc., selon ce qui est explicitement défini dans le Périmètre.

Les modalités précises (dates, plages horaires, périmètre détaillé, types de Tests) sont fixées dans le devis, la proposition commerciale, la lettre de mission ou tout document contractuel associé.

5. PERIMETRE DES TESTS

Le Client s'engage à communiquer au Prestataire un Périmètre clair et documenté, comprenant notamment :
- Les plages d'adresses IP, noms de domaine, réseaux, VLAN, etc. ;
- Les applications, services et systèmes concernés (y compris, le cas échéant, systèmes industriels/OT : automates, SCADA, protocoles industriels tels que Modbus, DNP3, IEC-60870-5-104, OPC UA, Profinet, etc.) ;
- Les environnements ciblés (production, préproduction, recette, développement...).

Tout système non explicitement inclus dans le Périmètre est considéré comme hors Tests.
Toute extension de Périmètre doit être validée par écrit (email, avenant, ticket officiel...) par le Client et le Prestataire.

6. INFORMATION DÉTAILLÉE SUR LES RISQUES

Le Client reconnaît que les Tests sont intrusifs par nature et que, malgré toutes les précautions raisonnables, ils peuvent entraîner notamment, sans que cette liste soit limitative :

1. Ralentissements, latences, dégradations de performances, congestions réseau, voire indisponibilités temporaires de services, applications ou équipements ;
2. Blocages de comptes, verrouillage de sessions, dysfonctionnements d'authentification (SSO, MFA, LDAP, AD, etc.) ;
3. Déclenchement ou surcharge de mécanismes de sécurité (IDS/IPS, WAF, EDR, antivirus, SIEM, SOC, firewall, filtrage industriel, etc.), pouvant provoquer des blocages ou actions automatiques ;
4. Corruption, altération ou perte de données, notamment en cas de combinaison imprévue de vulnérabilités ou de fragilité préexistante des systèmes ;
5. Modifications involontaires de configuration (réseau, systèmes, applications) résultant de l'exploitation de vulnérabilités ou de comportements internes des solutions utilisées par le Client ;
6. Rotation accrue ou saturation de logs, rendant plus difficile l'analyse ultérieure ;
7. Impacts sur systèmes industriels/OT, par exemple :
   - Arrêt ou perturbation d'automates, IHM, SCADA ;
   - Perturbation de processus de production ou de chaînes automatisées ;
   - Risques indirects sur la sécurité des personnes en cas de protections physiques ou logiques insuffisantes ;
8. Effets collatéraux sur des systèmes interconnectés non ou mal documentés par le Client (VPN, tunnels, routage, interconnexions multi-sites, etc.) ;
9. Révélation ou aggravation de dysfonctionnements préexistants (matériels vieillissants, logiciels obsolètes, systèmes non maintenus, infrastructures non redondées, etc.).

7. PERIODE DE RISQUE

Les Parties reconnaissent qu'un risque résiduel existe :
- Pendant la réalisation des Tests ;
- Et après, du fait d'effets différés (saturation progressive, redémarrage d'équipements, rotation de journaux, etc.) ou de l'exploitation possible par des tiers de vulnérabilités identifiées et non corrigées.

À ce titre, les Parties conviennent de définir une Période de Risque :
- Début : date de démarrage des Tests ;
- Fin : la plus tardive des dates suivantes :
  - X jours après la fin des Tests,
  - ou la date à laquelle le Client a mis en œuvre les mesures de remédiation prioritaires recommandées par le Prestataire.

Durant cette Période de Risque, certains incidents peuvent être liés à l'état initial du système d'information, aux vulnérabilités mises en évidence ou à leur non-correction.

8. OBLIGATIONS DU CLIENT

Avant le lancement des Tests, le Client s'engage à :
1. Mettre en place des sauvegardes complètes, récentes, testées et restaurables des systèmes et données critiques ;
2. Assurer, autant que possible, des mécanismes de continuité (redondance, PRA/PCA) pour les services sensibles ;
3. Informer précisément le Prestataire de l'existence de systèmes critiques, notamment industriels/OT et/ou impactant la sécurité des personnes ;
4. Limiter, dans la mesure du possible, les changements majeurs d'infrastructure pendant les Tests ;
5. Fournir au Prestataire des comptes dédiés lorsque cela est envisageable (comptes de test, comptes techniques) ;
6. Désigner un référent technique joignable pendant les Tests, capable de prendre des décisions rapides (pause, arrêt, modification de fenêtres d'intervention).

Le Client est seul responsable de :
- La validité juridique de l'autorisation donnée ;
- L'exactitude des informations communiquées sur son système d'information ;
- La mise en œuvre des corrections et remédiations recommandées.

9. OBLIGATIONS DU PRESTATAIRE

Le Prestataire s'engage à :
1. Mettre en œuvre une obligation de moyens, conformément aux usages et bonnes pratiques du métier (OWASP, recommandations d'organismes spécialisés, etc.) ;
2. Adapter l'intensité des Tests en fonction des comportements observés sur le système du Client ;
3. Suspendre ou interrompre des Tests en cas de dysfonctionnement majeur constaté ou signalé ;
4. Traiter comme confidentielles les informations techniques et les vulnérabilités identifiées ;
5. Utiliser les données collectées uniquement dans le cadre de la mission et pour la production des livrables.

10. LIMITATION ET EXCLUSION DE RESPONSABILITE

10.1. Principes généraux

Dans les limites permises par le droit applicable, le Prestataire - et notamment, le cas échéant, SYLink Technologie - ne pourra être tenu responsable des dommages subis par le Client lorsque :
- Les Tests ont été réalisés dans le Périmètre et le cadre convenus ;
- Le Prestataire a respecté son obligation de moyens et les bonnes pratiques.

10.2. Exclusions spécifiques

Le Prestataire ne pourra notamment pas être tenu responsable des dommages résultant :
1. De l'absence ou insuffisance de sauvegardes ou de dispositifs de continuité mis en place par le Client ;
2. De dysfonctionnements préexistants (pannes, instabilités, obsolescence, défaut de maintenance) révélés ou aggravés par les Tests ;
3. De l'utilisation de matériels ou logiciels obsolètes ou non supportés ;
4. Du non-respect par le Client des prérequis, consignes ou recommandations (avant, pendant, après les Tests) ;
5. D'actions ou omissions de prestataires tiers du Client (hébergeurs, intégrateurs, opérateurs, mainteneurs industriels, etc.) ;
6. De l'exploitation, par des attaquants réels, de vulnérabilités identifiées par le Prestataire et portées à la connaissance du Client mais non corrigées dans des délais raisonnables.

10.3. Dommages indirects

Le Prestataire ne pourra être tenu responsable des dommages indirects ou immatériels tels que :
- Perte de chiffre d'affaires ou de marge ;
- Perte de données non sauvegardées ;
- Perte de contrats ;
- Atteinte à l'image ou à la réputation ;
- Réclamations de tiers.

10.4. Plafond de responsabilite

Sous réserve des dispositions d'ordre public (faute lourde, dol, dommage corporel), la responsabilité globale et cumulée du Prestataire, toutes causes confondues, est expressément plafonnée au montant total hors taxes des sommes effectivement facturées et encaissées au titre de la mission de Tests concernée.

Lorsque le Prestataire est la société SYLink Technologie SAS, ce plafond s'applique intégralement comme limite maximale d'engagement financier.

11. LIVRABLES

À l'issue des Tests, le Prestataire peut fournir au Client :
- Un rapport technique détaillé (vulnérabilités, preuves de concept, chemins d'attaque, impacts, détails OT le cas échéant) ;
- Un rapport de synthèse à destination des instances managériales (priorisation des risques, recommandations, axes de remédiation).

Les livrables et leur niveau de détail sont définis dans la proposition commerciale ou le contrat spécifique.

12. CONFIDENTIALITE

Le Prestataire traite les informations du Client comme confidentielles et met en place des mesures de protection (contrôle d'accès, chiffrement, durée de conservation limitée...).

Le Client s'engage à ne pas publier les rapports du Prestataire de manière intégrale sans accord, et à anonymiser si nécessaire les éléments sensibles.

13. ACCEPTATION EXPRESSE DES RISQUES

En validant une prestation de Tests, en signant un devis ou en cochant la case "J'accepte les CGU et les risques associés", le Client :
- Reconnaît avoir lu et compris les présentes CGU ;
- Reconnaît avoir été informé de manière claire et détaillée des risques liés aux Tests (articles 6 à 8) ;
- Accepte expressément ces risques, y compris pendant la Période de Risque ;
- Accepte les limitations et exclusions de responsabilité prévues à l'article 10 ;
- Renonce, dans les limites du droit applicable, à rechercher la responsabilité du Prestataire, notamment de SYLink Technologie, pour les dommages exclus ou plafonnés dans les présentes CGU.

14. DROIT APPLICABLE ET JURIDICTION

Les présentes CGU sont soumises au droit français.
Tout litige relatif à leur validité, interprétation ou exécution, après tentative de résolution amiable, sera soumis aux tribunaux compétents de [Ville], nonobstant pluralité de défendeurs ou appel en garantie.`;

const CGUModal = ({ isOpen, onClose, onAccept, scanType = 'test' }) => {
  const [accepted, setAccepted] = useState(false);
  const [showFullCGU, setShowFullCGU] = useState(false);

  const handleAccept = () => {
    if (accepted) {
      onAccept();
      onClose();
    }
  };

  const handleDownload = () => {
    const blob = new Blob([CGU_CONTENT], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'CGU_SYLink_Pentest.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-brand-black-light border-2 border-yellow-500/50 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl shadow-yellow-500/20">
        {/* Header */}
        <div className="p-6 border-b border-yellow-500/30 bg-gradient-to-r from-yellow-500/10 to-orange-500/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-yellow-500/20 rounded-xl">
                <AlertTriangle className="w-8 h-8 text-yellow-500" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Validation Requise</h2>
                <p className="text-gray-400 text-sm mt-1">Acceptation des conditions générales d'utilisation</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-brand-black rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-400 hover:text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Warning Banner */}
          <div className="bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/50 rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 p-2 bg-red-500/20 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-red-400 mb-2">Avertissement Important</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Vous etes sur le point de lancer un <span className="text-white font-bold">{scanType}</span>.
                  Cette operation peut avoir des impacts sur les systemes cibles, incluant des ralentissements,
                  des interruptions de service, ou des modifications de configuration.
                </p>
                <p className="text-gray-400 text-sm mt-2">
                  Assurez-vous d'avoir les autorisations necessaires pour effectuer ces tests sur les systemes cibles.
                </p>
              </div>
            </div>
          </div>

          {/* Signature Box */}
          <div className="bg-brand-black rounded-xl border-2 border-brand-green/30 p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <FileText className="w-10 h-10 text-brand-green" />
              </div>
              <div className="flex-1">
                <p className="text-white text-base leading-relaxed">
                  En validant ma commande / ma demande de Tests, je déclare avoir lu et accepté les{' '}
                  <span className="text-brand-green font-bold">conditions générales d'utilisation</span>{' '}
                  du service de tests d'intrusion et d'audit de sécurité, ainsi que les risques décrits,
                  et j'accepte les limitations de responsabilité du Prestataire.
                </p>
              </div>
            </div>
          </div>

          {/* CGU Actions */}
          <div className="flex items-center justify-between bg-brand-black rounded-lg p-4 border border-gray-800">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-gray-400" />
              <span className="text-gray-300">Conditions générales d'utilisation</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors text-sm"
              >
                <Download className="w-4 h-4" />
                Telecharger
              </button>
              <button
                onClick={() => setShowFullCGU(!showFullCGU)}
                className="flex items-center gap-2 px-4 py-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 transition-colors text-sm"
              >
                {showFullCGU ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFullCGU ? 'Masquer' : 'Lire les CGU'}
              </button>
            </div>
          </div>

          {/* Full CGU Text */}
          {showFullCGU && (
            <div className="bg-brand-black rounded-xl border border-gray-800 overflow-hidden">
              <div className="p-4 border-b border-gray-800 bg-gray-900/50">
                <h3 className="text-lg font-bold text-brand-green flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Conditions générales d'utilisation - Service de Pentest
                </h3>
              </div>
              <div className="p-6 max-h-[400px] overflow-y-auto">
                <pre className="text-gray-300 text-sm whitespace-pre-wrap font-sans leading-relaxed">
                  {CGU_CONTENT}
                </pre>
              </div>
            </div>
          )}

          {/* Acceptance Checkbox */}
          <div className="bg-gradient-to-r from-brand-green/10 to-green-500/10 border-2 border-brand-green/50 rounded-xl p-5">
            <label className="flex items-start gap-4 cursor-pointer">
              <div className="flex-shrink-0 mt-1">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="w-6 h-6 accent-brand-green rounded cursor-pointer"
                />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium text-base">
                  J'ai lu et j'accepte les conditions générales d'utilisation
                </p>
                <p className="text-gray-400 text-sm mt-2">
                  En cochant cette case, vous confirmez avoir pris connaissance des CGU, des risques associés aux tests
                  d'intrusion, et vous acceptez les limitations de responsabilité du Prestataire (SYLink Technologie SAS).
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-800 bg-brand-black">
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors font-medium"
            >
              Annuler
            </button>
            <button
              onClick={handleAccept}
              disabled={!accepted}
              className={`px-8 py-3 rounded-lg font-bold flex items-center gap-2 transition-all ${
                accepted
                  ? 'bg-gradient-to-r from-brand-green to-green-600 text-brand-black hover:from-green-600 hover:to-brand-green shadow-lg shadow-brand-green/30'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              <CheckCircle className="w-5 h-5" />
              Valider et Lancer le Test
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CGUModal;
